// ============================================================
// Public School Context & Provider
// ============================================================
// Resolves and holds the public school configuration for the current hostname.
// Used across both Public School Website and ERP Login page.

import { createContext, useContext, useEffect, useState } from 'react';
import { websiteConfigService } from '@/services/websiteConfigService';
import { mockSchoolWebsiteData } from '@/mock/mockSchoolWebsiteData';

const PublicSchoolContext = createContext(null);

export function PublicSchoolProvider({ children }) {
  const [publicSchoolData, setPublicSchoolData] = useState(mockSchoolWebsiteData);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadConfig() {
      setIsLoading(true);

      // Skip fetching public school config on Super Admin routes to prevent unnecessary 404s
      if (window.location.pathname.startsWith('/super-admin')) {
        setPublicSchoolData(mockSchoolWebsiteData);
        setIsLoading(false);
        return;
      }

      try {
        const config = await websiteConfigService.fetchResolvedWebsiteConfig();
        if (isMounted) {
          setPublicSchoolData(config);
          setIsError(false);

          // Dynamically update document title if available
          if (config.school_name) {
            document.title = `${config.school_name} | Official Website & ERP`;
          }

          // Dynamically update favicon if available
          if (config.favicon) {
            let faviconLink = document.querySelector("link[rel*='icon']");
            if (!faviconLink) {
              faviconLink = document.createElement('link');
              faviconLink.rel = 'icon';
              document.head.appendChild(faviconLink);
            }
            faviconLink.href = config.favicon;
          }
        }
      } catch (err) {
        console.error('Error fetching public school config:', err);
        if (isMounted) {
          setPublicSchoolData(mockSchoolWebsiteData);
          setIsError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <PublicSchoolContext.Provider
      value={{
        publicSchoolData,
        isLoading,
        isError,
        refetchConfig: async () => {
          setIsLoading(true);
          const config = await websiteConfigService.fetchResolvedWebsiteConfig();
          setPublicSchoolData(config);
          setIsLoading(false);
        },
      }}
    >
      {children}
    </PublicSchoolContext.Provider>
  );
}

export function usePublicSchool() {
  const context = useContext(PublicSchoolContext);
  if (!context) {
    throw new Error('usePublicSchool must be used within a PublicSchoolProvider');
  }
  return context;
}

export default PublicSchoolContext;
